import React, { useState, useEffect, useContext } from 'react';
import { t } from 'i18next';
import { ProfileHeaderContent, EmailChange } from 'templates';
import {
  ToggleSwitch,
  GlobalSuccess,
  Button,
  Loader,
  ModalOverlay,
  Headings,
  Paragraph,
} from '@sentara-health/optimahealth_web';
import {
  isUnAuthorizedError,
  unAuthorizedErrorHandler,
  ANALYTICS_MESSAGES,
  domainForSegment,
  useAnalytics,
} from 'common';
import TextNotificationWrapper from './TextNotificationWrapper';
import { CommunicationChannelsProps } from './interface';
import { GlobalContext } from '../../context';
import { usePreferenceUpdate } from '@sentara-health/sentara-api-hooks-core';
import { usePreferenceSearchAPI } from 'common/utils/PreferenceSearchAPIUtil';
import { updatePreferenceOnlyHelper } from './updatePreferenceHelper';

const CommunicationChannels: React.FC<CommunicationChannelsProps> = ({
  smsConsent,
  memberView = false,
}) => {
  const [success, setSuccess] = useState(false);
  const [showEdit, setShowEdit] = useState(true);
  const [email, setEmail] = useState<string>('');
  const [selectedChannel, setSelectedChannel] = useState<
    'Email' | 'Text' | null
  >(null);
  const [isEmail, setIsEmail] = useState(false);
  const [isText, setIsText] = useState(false);
  const [validEmailModal, setValidEmailModal] = useState(false);
  const [validSmsModal, setValidSmsModal] = useState(false);
  const [changeButtonState, setChangeButtonState] = useState(false);
  const [loading, setLoading] = useState(false);
  const [canDoApiCall, setCanDoApiCall] = useState(false);
  const [initialEmailState, setInitialEmailState] = useState(isEmail);
  const [initialTextState, setInitialTextState] = useState(isText);

  const { updatePreference } = usePreferenceUpdate();
  const { getFeature, loginMemberId, setProfileState, profileState } =
    useContext(GlobalContext);
  const { trackContentListing } = useAnalytics(`${domainForSegment}/profile`);

  const [phone, setPhone] = useState<string>(
    profileState?.smsConsent?.mobileNumber ?? ''
  );
  const emailConsent = getFeature?.EmailConsent ?? false;
  const textNotificationsFeature = getFeature?.TextNotifications ?? false;
  const { fetchProfile } = usePreferenceSearchAPI();

  useEffect(() => {
    let emailVal = '';

    if (Array.isArray(profileState?.email)) {
      emailVal = profileState?.email[0]?.value ?? '';
    } else if (
      typeof profileState?.email === 'object' &&
      profileState?.email !== null
    ) {
      emailVal = (profileState?.email as { value?: string }).value ?? '';
    } else if (typeof profileState?.email === 'string') {
      emailVal = profileState?.email;
    }

    setEmail(emailVal);
    setPhone(profileState?.smsConsent?.mobileNumber ?? '');
  }, [profileState?.email, profileState?.smsConsent]);

  const handleSuccess = (email: string) => {
    setShowEdit(true);
    setEmail(email);
    setSuccess(true);
  };
  useEffect(() => {
    if (profileState?.preferenceChannel === 'None') {
      setIsEmail(false);
      setIsText(false);
    }
    if (profileState?.preferenceChannel === 'email') {
      setIsEmail(true);
      setIsText(false);
    }
    if (profileState?.preferenceChannel === 'text') {
      setIsText(true);
      setIsEmail(false);
    }
    if (profileState?.preferenceChannel === 'email,text') {
      setIsEmail(true);
      setIsText(true);
    }
  }, [profileState, showEdit]);

  useEffect(() => {
    const hasChanges = !(
      isEmail === initialEmailState && isText === initialTextState
    );
    setChangeButtonState(hasChanges);
    setCanDoApiCall(hasChanges);
  }, [isEmail, isText, initialEmailState, initialTextState]);

  const handleButtonAction = () => {
    trackContentListing('610', ANALYTICS_MESSAGES.PREF_CHANNEL_EDIT, 12);
    setInitialEmailState(isEmail);
    setInitialTextState(isText);
    setShowEdit(false);
  };

  const saveButtonPressed = async () => {
    if (canDoApiCall) {
      setLoading(true);
      let preferenceOption = '';
      if (isEmail && isText) {
        preferenceOption = 'email,text';
      } else if (isEmail && !isText) {
        preferenceOption = 'email';
      } else if (!isEmail && isText) {
        preferenceOption = 'text';
      } else {
        preferenceOption = 'None';
      }
      setLoading(true);

      const apiPayload = updatePreferenceOnlyHelper(
        loginMemberId ?? '',
        preferenceOption
      );
      const response = await updatePreference(apiPayload);
      if (response?.error) {
        setLoading(false);
        trackContentListing(
          '613',
          ANALYTICS_MESSAGES.PREF_CHANNEL_SAVE_FAILURE,
          15
        );
        if (response?.errorCode && isUnAuthorizedError(response?.errorCode)) {
          unAuthorizedErrorHandler();
        }
        return;
      }

      trackContentListing(
        isEmail ? '611' : '612',
        isEmail
          ? ANALYTICS_MESSAGES.PREF_CHANNEL_SELECT_EMAIL
          : ANALYTICS_MESSAGES.PREF_CHANNEL_SELECT_TEXT,
        isEmail ? 13 : 14
      );
      fetchProfile({
        onSuccess: () => {
          setShowEdit(true);
          setLoading(false);
          setSuccess(true);
        },
      });
    }
  };

  const getSuccessMessage = () => {
    const baseMessage = t('profile.optedCommunicationChannelSuccess');

    let selectedChannel;
    if (isEmail && isText) {
      selectedChannel = 'Both';
    } else if (isEmail) {
      selectedChannel = 'Email';
    } else if (isText) {
      selectedChannel = 'Text';
    } else {
      selectedChannel = 'None';
    }

    switch (selectedChannel) {
      case 'Both':
        return `${baseMessage} ${t('profile.optedToEmailAndText')}`;
      case 'Email':
        return `${baseMessage} ${t('profile.optedToEmail')}`;
      case 'Text':
        return `${baseMessage} ${t('profile.optedToText')}`;
      case 'None':
        return `${baseMessage}${t('profile.optedNone')}`;
      default:
        return baseMessage;
    }
  };

  const cancelButtonPressed = () => {
    trackContentListing('614', ANALYTICS_MESSAGES.PREF_CHANNEL_CANCEL, 16);
    // Revert toggles to their initial states
    setIsEmail(initialEmailState);
    setIsText(initialTextState);
    setValidEmailModal(false);
    setValidSmsModal(false);
    setShowEdit(true);
  };

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(false);
        setSelectedChannel(null);
      }, 6000);

      return () => clearTimeout(timer);
    }
  }, [success]);

  return (
    <div>
      {textNotificationsFeature || emailConsent ? (
        <hr className="hr_line" />
      ) : null}
      {loading && <Loader />}

      <div className="col-sm-12 col-12">
        <ProfileHeaderContent
          header={t('profile.communicationPreferences')}
          contentOne={t('profile.communicationPreferencesdesc') ?? ''}
          dataTestIdOne="profileName"
          showButton={showEdit && !selectedChannel}
          onClick={() => handleButtonAction()}
          contentClassNameOne={'mb-3 mb-lg-0 mb-md-0'}
          disableButton={!profileState?.isMemberEnrolled}
        />
      </div>

      {!showEdit ? (
        <div>
          <ul className="pref_choose_options d-flex mt-2 flex-lg-row flex-md-row mb-4">
            <li>
              <ToggleSwitch
                checked={isEmail}
                onChange={() => {
                  if (
                    !isEmail &&
                    (!profileState?.emailConsent.optInConsent ||
                      (!profileState?.isEmailValid &&
                        getFeature?.EmailBounceBack)) // If the Email is not Valid we show a modal and go with email flow.
                  ) {
                    setValidEmailModal(true);
                  } else if (isText && !profileState?.smsConsent.optInConsent) {
                    // if the user is working on the SMS flow, when he tries to toggle the Email toggle, it reverts back the text toggle
                    setIsText(false);
                    setIsEmail(!isEmail);
                  } else {
                    setIsEmail(!isEmail);
                  }
                }}
              >
                {t('common.email')}
              </ToggleSwitch>
            </li>
            <li className="col-md-6 col-6">
              <ToggleSwitch
                checked={isText}
                onChange={() => {
                  if (!isText && !profileState?.smsConsent.optInConsent) {
                    // If the Text notification is opted out we show a modal and go with Text Notifications flow.
                    setValidSmsModal(true);
                  } else if (
                    // if the user is working on the Email flow, when he tries to toggle the Text toggle, it reverts back the email toggle
                    isEmail &&
                    (!profileState?.emailConsent.optInConsent ||
                      (!profileState?.isEmailValid &&
                        getFeature?.EmailBounceBack)) // If the Email is not Valid we show a modal
                  ) {
                    setIsEmail(false);
                    setIsText(!isText);
                  } else {
                    setIsText(!isText);
                  }
                }}
              >
                {t('common.text')}
              </ToggleSwitch>
            </li>
          </ul>
          <div className="font-size-16 ms-1 italic-style">
            {t('profile.communicationPreferencesdescsub')}
          </div>

          {isEmail &&
          !profileState?.isEmailValid &&
          getFeature?.EmailBounceBack ? (
            <div className="communication_preference_wrapper">
              <hr />
              <ProfileHeaderContent
                data-testid="profile-header-component"
                header={t('common.emailAddress')}
                contentOne={email ?? ''}
                dataTestIdOne="profileName"
                contentClassNameOne={'_disabled mb-4'}
                disableButton={!profileState?.isMemberEnrolled}
              />
              <EmailChange
                emailId={email ?? ''}
                customButton={false}
                onClickCancel={() => {
                  cancelButtonPressed();
                  setShowEdit(true);
                  setSelectedChannel(null);
                }}
                onClickSubmit={() => saveButtonPressed()}
                handleSuccess={handleSuccess}
                memberView={memberView}
              />
            </div>
          ) : null}

          {isText && !profileState?.smsConsent?.optInConsent ? (
            <div className="communication_preference_wrapper">
              <hr />
              <TextNotificationWrapper
                smsConsent={smsConsent}
                setState={setProfileState}
                state={profileState}
                showPrimaryNumberProp={true}
                isCommunicationChannels={true}
                cancelConsentProp={() => {
                  setShowEdit(true);
                  setSelectedChannel(null);
                }}
                fromPreferredChannel={true}
                currentTextState={isText}
                onSubmitSuccess={() => {
                  setSuccess(true);
                  setShowEdit(true);
                }}
              />
            </div>
          ) : null}

          {(!isEmail ||
            profileState?.isEmailValid ||
            (!profileState?.isEmailValid && !getFeature?.EmailBounceBack)) &&
          (!isText || profileState?.smsConsent?.optInConsent) ? (
            <>
              <hr />
              <div className="row d-flex justify-content-start align-items-center py-2">
                <div className="col-sm-auto col-12 mb-sm-0 mb-4">
                  <Button
                    dataTestId={'saveNextButton'}
                    variant="primary_btn"
                    disabled={!changeButtonState}
                    type={'button'}
                    onClick={saveButtonPressed}
                  >
                    {t('common.save')}
                  </Button>
                </div>
                <div className="col-sm-auto col-12 mb-sm-0">
                  <Button
                    dataTestId={'emailCancelButton'}
                    variant="secondary_btn"
                    onClick={cancelButtonPressed}
                  >
                    {t('common.cancel')}
                  </Button>
                </div>
              </div>
            </>
          ) : null}
        </div>
      ) : null}
      {validSmsModal && (
        <ModalOverlay
          isOpen={validSmsModal}
          onClose={() => {
            setValidSmsModal(false);
          }}
          overlayInfo="Modal information"
        >
          <div data-testid="amSurePopUp">
            <Headings
              level={2}
              text={t('profile.signupNotifications')}
              variant="regular"
            />
            <Paragraph
              text={t('profile.textNotificationsDesc')}
              variant="regular"
              data-testId="iamSureContent"
              className="mb-4 pb-2"
            />
            <div className="row d-flex justify-content-start align-items-center py-2">
              <div className="col-sm-auto col-12 mb-sm-0 mb-4">
                <Button
                  dataTestId={'imSureButton'}
                  variant="primary_btn"
                  type="submit"
                  onClick={() => {
                    setValidSmsModal(false);
                    setIsText(true);
                  }}
                >
                  {t('common.signUp')}
                </Button>
              </div>
              <div className="col-sm-auto col-12 mb-sm-0">
                <Button
                  dataTestId={'cancelButton'}
                  variant="secondary_btn"
                  onClick={() => {
                    setValidSmsModal(false);
                    // Keep isText as false (revert the change)
                    setIsText(initialTextState);
                  }}
                >
                  {t('common.cancel')}
                </Button>
              </div>
            </div>
          </div>
        </ModalOverlay>
      )}
      {validEmailModal && (
        <ModalOverlay
          isOpen={validEmailModal}
          onClose={() => {
            setValidEmailModal(false);
          }}
          overlayInfo="Modal information"
        >
          <div data-testid="amSurePopUp">
            <Headings
              level={2}
              text={t('profile.emailconsentRequired')}
              variant="regular"
            />
            <Paragraph
              text={t('profile.emailconsentRequiredDesc')}
              variant="regular"
              data-testId="iamSureContent"
              className="mb-4 pb-2"
            />
            <div className="d-flex flex-lg-row flex-md-row flex-column gap-lg-3 gap-md-3 gap-4 mb-2">
              <Button
                dataTestId={'imSureButton'}
                variant="primary_btn"
                type="submit"
                onClick={() => {
                  setValidEmailModal(false);
                  setIsEmail(true);
                }}
              >
                {t('profile.phoneAcknowledgementPopUpSignup')}
              </Button>
              <Button
                dataTestId={'signupButton'}
                variant="secondary_btn"
                onClick={() => {
                  setValidEmailModal(false);
                  setIsEmail(initialEmailState);
                }}
              >
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        </ModalOverlay>
      )}
      {success ? (
        <div className="fade-toast mt-3">
          <GlobalSuccess variant="mb-3" dataTestId="usernameServiceErr12">
            {getSuccessMessage()}
          </GlobalSuccess>
        </div>
      ) : null}
    </div>
  );
};

export default CommunicationChannels;
